<script lang="ts" setup>
import { QChip } from 'quasar'
import { computed } from 'vue'
import { Color } from '@previo/codebooks'
import PIcon from '../icon/PIcon.vue'
import PSpace from '../space/PSpace.vue'
import { PSpaceSize } from '../space/types'
import PTextBadge from '../typography/system/PTextBadge.vue'
import { PMaxLines } from '../typography/types'

type Props = {
  text?: string | number
  color:
    | Color.PRIMARY
    | Color.POSITIVE
    | Color.WARNING
    | Color.NEGATIVE
    | Color.INFO
    | Color.GREY_600
  iconStart?: null | string
  iconEnd?: null | string
  grow?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  iconStart: null,
  iconEnd: null,
  grow: false,
})

const colorText = computed<
  | Color.PRIMARY_DARK
  | Color.POSITIVE_DARK
  | Color.WARNING_DARK
  | Color.NEGATIVE_STATE
  | Color.INFO_DARK
  | Color.GREY_500
>(() => {
  switch (props.color) {
    case Color.PRIMARY:
      return Color.PRIMARY_DARK
    case Color.POSITIVE:
      return Color.POSITIVE_DARK
    case Color.WARNING:
      return Color.WARNING_DARK
    case Color.NEGATIVE:
      return Color.NEGATIVE_STATE
    case Color.INFO:
      return Color.INFO_DARK
    case Color.GREY_600:
      return Color.GREY_500
  }

  throw 'Invalid prop of color in Ptag'
})

const colorBg = computed<
  | Color.PRIMARY_TRANSPARENT
  | Color.POSITIVE_TRANSPARENT
  | Color.WARNING_TRANSPARENT
  | Color.NEGATIVE_TRANSPARENT
  | Color.INFO_TRANSPARENT
  | Color.GREY_100
>(() => {
  switch (props.color) {
    case Color.PRIMARY:
      return Color.PRIMARY_TRANSPARENT
    case Color.POSITIVE:
      return Color.POSITIVE_TRANSPARENT
    case Color.WARNING:
      return Color.WARNING_TRANSPARENT
    case Color.NEGATIVE:
      return Color.NEGATIVE_TRANSPARENT
    case Color.INFO:
      return Color.INFO_TRANSPARENT
    case Color.GREY_600:
      return Color.GREY_100
  }

  throw 'Invalid prop of color in Ptag'
})
</script>

<template>
  <q-chip
    ref="qChipComponent"
    :ripple="false"
    :color="colorBg"
    :text-color="colorText"
    :class="['ds-tag', { 'p-fill-available-width': grow }]"
  >
    <slot name="default">
      <p-icon v-if="iconStart" :icon="iconStart" />

      <template v-if="text">
        <p-space :mobile="PSpaceSize.XS" />
        <p-text-badge :text="text" :max-lines="PMaxLines.ONE" />
        <p-space :mobile="PSpaceSize.XS" />
      </template>

      <p-icon v-if="iconEnd" :icon="iconEnd" />
    </slot>
  </q-chip>
</template>

<style lang="scss" scoped>
@import '../../assets/scss/shared/ds.variables.module';

.ds-tag {
  margin: 0 0;
  border-radius: $global-border-radius;
}
</style>
